// GLSL ES 3.00 shaders for the globe. Everything is in "globe space": a unit
// sphere with +Y north, prime meridian at +Z, +X at 90°E.

/**
 * Smooth visibility of a point p as seen from cam: 1 when the straight line to the
 * camera is clear, fading to 0 as the line passes deeper through the planet.
 */
const OCCLUSION = /* glsl */ `
float occlusion(vec3 p, vec3 cam) {
  vec3 d = p - cam;
  float L = length(d);
  vec3 dir = d / L;
  float b = dot(cam, dir);
  float c = dot(cam, cam) - 1.0;
  float disc = b * b - c;
  if (disc <= 0.0) return 1.0;
  float s = sqrt(disc);
  float t0 = -b - s;
  float t1 = -b + s;
  float inside = clamp(min(L, t1) - max(t0, 0.0), 0.0, 2.0);
  return 1.0 - smoothstep(0.0, 0.12, inside);
}
`;

// ---------------------------------------------------------------- ocean + atmosphere

export const GLOBE_VS = /* glsl */ `#version 300 es
in vec2 aCorner;
uniform vec4 uQuad; // center ndc xy, half size ndc xy
out vec2 vNdc;
void main() {
  vec2 p = uQuad.xy + aCorner * uQuad.zw;
  vNdc = p;
  gl_Position = vec4(p, 0.0, 1.0);
}
`;

export const GLOBE_FS = /* glsl */ `#version 300 es
precision highp float;
in vec2 vNdc;
uniform mat4 uInvMVP;
uniform vec3 uCam;
uniform vec3 uSun;
uniform float uEnergy;
uniform float uTime;
out vec4 outColor;

const float PI = 3.14159265;

float gridLine(float coord, float spacing) {
  float f = coord / spacing;
  float d = abs(fract(f - 0.5) - 0.5);
  float w = fwidth(f);
  return 1.0 - smoothstep(0.0, w * 1.2, d);
}

void main() {
  vec4 fp = uInvMVP * vec4(vNdc, 1.0, 1.0);
  vec3 far = fp.xyz / fp.w;
  vec3 ro = uCam;
  vec3 rd = normalize(far - ro);
  float b = dot(ro, rd);
  float c = dot(ro, ro) - 1.0;
  float disc = b * b - c;
  float h = sqrt(max(dot(ro, ro) - b * b, 0.0)); // closest approach to the centre
  vec3 closest = normalize(ro - rd * b);
  float sunSide = dot(closest, uSun);

  // ---- atmosphere halo (outside silhouette), additive
  float e = max(h - 1.0, 0.0);
  float halo = exp(-e * 26.0) * 0.55 + exp(-e * 7.0) * 0.18;
  halo *= smoothstep(0.55, 0.0, e);
  float lit = smoothstep(-0.55, 0.65, sunSide);
  vec3 cyan = vec3(0.30, 0.78, 1.00);
  vec3 violet = vec3(0.42, 0.26, 0.95);
  vec3 haloCol = mix(violet, cyan, lit) * halo * (0.45 + 0.75 * lit) * (1.0 + 0.6 * uEnergy);
  // warm scattering where the terminator meets the limb
  haloCol += vec3(1.0, 0.45, 0.15) * exp(-pow(sunSide / 0.22, 2.0)) * exp(-e * 22.0) * 0.22;

  // ---- sphere surface
  float fw = max(fwidth(h), 1e-4);
  float cov = clamp((1.0 - h) / fw + 0.5, 0.0, 1.0);
  vec3 col = vec3(0.0);
  if (cov > 0.0) {
    float t = -b - sqrt(max(disc, 0.0));
    vec3 p = ro + rd * t;
    vec3 n = normalize(p);
    vec3 v = -rd;
    float ndl = dot(n, uSun);
    float ndv = clamp(dot(n, v), 0.0, 1.0);
    float day = smoothstep(-0.25, 0.7, ndl);
    col = mix(vec3(0.006, 0.009, 0.030), vec3(0.020, 0.052, 0.125), day);

    // graticule (15°) — faint holographic lattice
    float lat = asin(clamp(n.y, -1.0, 1.0)) * 180.0 / PI;
    float lon = atan(n.x, n.z) * 180.0 / PI;
    float g = max(gridLine(lat, 15.0), gridLine(lon, 15.0) * smoothstep(88.0, 70.0, abs(lat)));
    col += vec3(0.20, 0.45, 0.95) * g * 0.050 * ndv * (0.6 + 0.4 * day);

    // fresnel rim
    float fres = pow(1.0 - ndv, 3.0);
    vec3 rim = mix(violet, cyan, smoothstep(-0.5, 0.5, ndl));
    col += rim * fres * (0.35 + 0.55 * smoothstep(-0.4, 0.5, ndl)) * (1.0 + 0.7 * uEnergy);

    // sun glint
    vec3 hv = normalize(uSun + v);
    float spec = pow(max(dot(n, hv), 0.0), 70.0) * smoothstep(0.0, 0.25, ndl);
    float sheen = pow(max(dot(n, hv), 0.0), 8.0) * smoothstep(0.0, 0.25, ndl);
    col += vec3(0.55, 0.75, 1.0) * (spec * 0.22 + sheen * 0.035);

    // twilight band
    col += vec3(1.0, 0.45, 0.16) * exp(-pow((ndl + 0.02) / 0.16, 2.0)) * 0.03 * (0.4 + 0.6 * ndv);
  }
  outColor = vec4(col * cov + haloCol * (1.0 - cov), cov);
}
`;

// ---------------------------------------------------------------- land dots

export const DOTS_VS = /* glsl */ `#version 300 es
in vec3 aPos;
in vec2 aAttr; // city light, random
uniform mat4 uMVP;
uniform vec3 uCam;
uniform vec3 uSun;
uniform float uPointSize;
uniform float uTime;
uniform float uEnergy;
out vec3 vColor;
${OCCLUSION}
void main() {
  vec3 p = aPos * 1.0015;
  gl_Position = uMVP * vec4(p, 1.0);
  vec3 v = normalize(uCam - p);
  float facing = dot(aPos, v);
  float vis = occlusion(p, uCam) * smoothstep(-0.02, 0.22, facing);
  float ndl = dot(aPos, uSun);
  float day = smoothstep(-0.10, 0.28, ndl);
  vec3 dayC = vec3(0.62, 0.90, 1.00);
  vec3 nightC = vec3(0.20, 0.30, 0.72);
  vec3 c = mix(nightC, dayC, day);
  float term = exp(-pow((ndl + 0.015) / 0.075, 2.0));
  c = mix(c, vec3(1.0, 0.64, 0.30), term * 0.85);
  float night = 1.0 - day;
  float city = aAttr.x * night;
  c = mix(c, vec3(1.0, 0.72, 0.40), clamp(city * 1.2, 0.0, 0.85));
  float bright = mix(0.5, 0.95, day) + term * 0.35 + city * 0.6;
  // slow holographic sweep + twinkle + audio energy
  float sweep = exp(-pow(fract(aPos.y * 0.5 + 0.5 - uTime * 0.045) - 0.5, 2.0) * 900.0);
  float tw = 0.88 + 0.12 * sin(uTime * (1.2 + aAttr.y * 2.0) + aAttr.y * 40.0);
  bright *= tw * (1.0 + 0.45 * uEnergy);
  bright += sweep * 0.22;
  vColor = c * bright * vis;
  gl_PointSize = uPointSize * (0.62 + 0.38 * clamp(facing, 0.0, 1.0)) * (1.0 + 0.25 * city) * (1.0 + 0.12 * uEnergy);
  if (vis <= 0.001) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
}
`;

export const DOTS_FS = /* glsl */ `#version 300 es
precision mediump float;
in vec3 vColor;
out vec4 outColor;
void main() {
  vec2 q = gl_PointCoord * 2.0 - 1.0;
  float r = length(q);
  float a = 1.0 - smoothstep(0.55, 1.0, r);
  if (a <= 0.0) discard;
  outColor = vec4(vColor * a, 0.0);
}
`;

// ---------------------------------------------------------------- ribbons (arcs, trails, beacon)

export const RIBBON_VS = /* glsl */ `#version 300 es
in vec3 aPos;
in vec3 aTan;
in vec2 aSideWidth; // side (-1..1), width in css px
in vec4 aColor;     // rgb, intensity
in vec2 aDash;      // u (arc param), dash speed (0 = solid)
uniform mat4 uMVP;
uniform vec3 uCam;
uniform vec2 uHalfRes; // half drawing-buffer size in px
uniform float uDpr;
out vec4 vColor;
out float vSide;
out vec2 vDash;
${OCCLUSION}
void main() {
  vec4 c0 = uMVP * vec4(aPos, 1.0);
  vec4 c1 = uMVP * vec4(aPos + aTan * 0.01, 1.0);
  vec2 s0 = c0.xy / c0.w * uHalfRes;
  vec2 s1 = c1.xy / c1.w * uHalfRes;
  vec2 d = s1 - s0;
  float len = length(d);
  vec2 dir = len > 1e-5 ? d / len : vec2(1.0, 0.0);
  vec2 nrm = vec2(-dir.y, dir.x);
  vec2 off = nrm * aSideWidth.x * aSideWidth.y * 0.5 * uDpr / uHalfRes;
  gl_Position = c0 + vec4(off * c0.w, 0.0, 0.0);
  float vis = occlusion(aPos, uCam);
  vColor = vec4(aColor.rgb, aColor.a * vis);
  vSide = aSideWidth.x;
  vDash = aDash;
}
`;

export const RIBBON_FS = /* glsl */ `#version 300 es
precision mediump float;
in vec4 vColor;
in float vSide;
in vec2 vDash;
uniform float uTime;
out vec4 outColor;
void main() {
  float x = vSide;
  float core = exp(-x * x * 22.0);
  float glow = exp(-x * x * 3.5) * (1.0 - x * x);
  float a = vColor.a;
  if (vDash.y > 0.0) {
    float s = sin(vDash.x * 90.0 - uTime * vDash.y);
    a *= 0.25 + 0.75 * smoothstep(-0.2, 0.6, s);
  }
  vec3 col = vColor.rgb * (glow * 0.55 + core * 0.9) + vec3(1.0) * core * 0.35 * min(a, 1.0);
  outColor = vec4(col * a, 0.0);
}
`;

// ---------------------------------------------------------------- sprites (instanced quads)

export const SPRITE_VS = /* glsl */ `#version 300 es
in vec2 aCorner;
in vec4 iPosSize;  // xyz, size (css px radius for billboards, world radius for tangent)
in vec4 iColor;    // rgb, intensity
in vec4 iParam;    // shape, mode (0 billboard, 1 tangent), p1, rotation
uniform mat4 uMVP;
uniform vec3 uCam;
uniform vec2 uHalfRes;
uniform float uDpr;
out vec2 vUv;
out vec4 vColor;
out vec4 vParam;
${OCCLUSION}
void main() {
  vec3 p = iPosSize.xyz;
  float size = iPosSize.w;
  vec4 clip;
  float fade = occlusion(p, uCam);
  if (iParam.y < 0.5) {
    clip = uMVP * vec4(p, 1.0);
    clip.xy += aCorner * size * uDpr / uHalfRes * clip.w;
  } else {
    vec3 n = normalize(p);
    vec3 up = abs(n.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    vec3 t = normalize(cross(up, n));
    vec3 bt = cross(n, t);
    vec3 wp = p + (t * aCorner.x + bt * aCorner.y) * size;
    clip = uMVP * vec4(wp, 1.0);
    float facing = dot(n, normalize(uCam - p));
    fade *= smoothstep(-0.05, 0.25, facing);
  }
  gl_Position = clip;
  vUv = aCorner;
  vColor = vec4(iColor.rgb, iColor.a * fade);
  vParam = iParam;
  if (vColor.a <= 0.001) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
}
`;

export const SPRITE_FS = /* glsl */ `#version 300 es
precision highp float;
in vec2 vUv;
in vec4 vColor;
in vec4 vParam;
out vec4 outColor;

float ring(float r, float radius, float th) {
  float aa = fwidth(r) * 1.2;
  return 1.0 - smoothstep(th, th + aa, abs(r - radius));
}

void main() {
  float r = length(vUv);
  if (r > 1.0) discard;
  int shape = int(vParam.x + 0.5);
  float p1 = vParam.z;
  float rot = vParam.w;
  vec3 col = vColor.rgb;
  float a = 0.0;
  vec3 outc;
  if (shape == 0) {
    // glowing dot: hot core + soft halo
    float core = 1.0 - smoothstep(0.14, 0.26, r);
    float halo = exp(-r * r * 7.0) * (1.0 - r);
    outc = mix(col, vec3(1.0), core * 0.65) * (core * 1.1 + halo * 0.75);
  } else if (shape == 1) {
    // thin ring (p1 = thickness)
    float th = max(p1, 0.02);
    float rr = ring(r, 0.82, th);
    float soft = exp(-pow((r - 0.82) / (th * 3.5 + 0.04), 2.0));
    outc = col * (rr * 0.9 + soft * 0.35);
  } else if (shape == 2) {
    // crosshair cursor
    float rr = ring(r, 0.5, 0.035);
    vec2 q = abs(vUv);
    float aa = fwidth(r) * 1.2;
    float lx = (1.0 - smoothstep(0.03, 0.03 + aa, q.y)) * step(0.66, q.x) * step(q.x, 0.95);
    float ly = (1.0 - smoothstep(0.03, 0.03 + aa, q.x)) * step(0.66, q.y) * step(q.y, 0.95);
    float dotc = 1.0 - smoothstep(0.08, 0.14, r);
    outc = mix(col, vec3(1.0), 0.25) * (rr + lx + ly + dotc) + col * exp(-r * r * 5.0) * 0.15;
  } else if (shape == 3) {
    // segmented rotating beacon ring (p1 = segments)
    float ang = atan(vUv.y, vUv.x) + rot;
    float segs = max(p1, 3.0);
    float f = fract(ang / 6.2831853 * segs);
    float gap = smoothstep(0.0, 0.06, f) * smoothstep(0.72, 0.62, f);
    float rr = ring(r, 0.84, 0.045) * gap;
    float inner = ring(r, 0.58, 0.018) * 0.55;
    float ticks = ring(r, 0.96, 0.02) * step(0.93, fract(ang / 6.2831853 * 24.0)) ;
    outc = col * (rr + inner + ticks * 0.8) + col * exp(-pow((r - 0.84) / 0.12, 2.0)) * 0.25;
  } else {
    // soft wide glow
    outc = col * exp(-r * r * 4.5) * (1.0 - r);
  }
  outColor = vec4(outc * vColor.a, 0.0);
}
`;
